import React, { useState, useEffect } from "react";
import { Eye, MapPin, User, Calendar, Edit } from "lucide-react";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useConfigMapData } from "@features/onboarding/utility-setup/hooks";
import { ServiceAreaFilterPanel } from "../filter/ServiceAreaFilter";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Switch } from "@shared/ui/switch";
import { usePatchConfigMap } from "../../../hooks";
import AddEntityModal from "../add/AddEntityModal";

interface ServiceAreaItem {
  page_number: Number;
  count: Number;
  next: string;
  previous: string;
  result: ServiceAreaDataItem[];
}

interface ServiceAreaDataItem {
  name: string;
  latitude: number | null;
  is_active: boolean;
  longitude: number | null;
  created_by: string;
  total_units: string;
  created_date: string;
  code: string;
  child_count: number;
  configurationCode?: string; // Optional, used for display
  hierarchy_names: {
    region?: string;
    country?: string;
    state?: string;
    county?: string;
    zone?: string;
    division?: string;
    area?: string;
    sub_area?: string;
    premise?: string;
  };
  hierarchy_codes: {
    region?: string;
    country?: string;
    state?: string;
    county?: string;
    zone?: string;
    division?: string;
    area?: string;
    sub_area?: string;
    premise?: string;
  };
  installed_meter_count?: number;
  billed_unit_count?: number;
}

interface ServiceAreaFilters {
  page: number;
  limit: number;
  remote_utility_id: number;
  config_level: string;
  show_inactive?: boolean;
  search_data?: string;
  created_by?: string;
  parent_name?: string;
  type_filter?: string;
  sort_by?: string;
  sort_order?: string;
  tab?: string; // Added to handle different tabs
}

interface ServiceAreaTableProps {
  entityType: string;
  configLevel: string;
  onEditEntity: (entity: ServiceAreaDataItem) => void;
}

const ServiceAreaTable: React.FC<ServiceAreaTableProps> = ({
  entityType,
  configLevel,
  onEditEntity,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const itemsPerPage = PAGE_SIZE;
  const [selectedEntity, setSelectedEntity] = useState<ServiceAreaItem | null>(
    null,
  );
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [entityToEdit, setEntityToEdit] = useState<any>(null);
  // Parse filters from URL params
  const getFiltersFromUrl = (): ServiceAreaFilters => {
    const page = parseInt(searchParams.get("page") || "1");
    const limit = parseInt(searchParams.get("limit") || itemsPerPage.toString());
    const urlFilters: ServiceAreaFilters = {
      page,
      limit,
      remote_utility_id: Number(remoteUtilityId),
      config_level: configLevel,
      tab: searchParams.get("tab") || "all", // Default to "all" if not set
    };

    // Parse show_inactive
    const showInactiveParam = searchParams.get("show_inactive");
    if (showInactiveParam !== null && showInactiveParam !== undefined) {
      urlFilters.show_inactive = showInactiveParam === "true";
    }

    // Parse created_by
    const createdByParam = searchParams.get("created_by");
    if (createdByParam) {
      urlFilters.created_by = createdByParam;
    }

    // Parse parent_name
    const parentNameParam = searchParams.get("parent_name");
    if (parentNameParam) {
      urlFilters.parent_name = parentNameParam;
    }

    // Parse type_filter
    const typeFilterParam = searchParams.get("type_filter");
    if (typeFilterParam) {
      urlFilters.type_filter = typeFilterParam;
    }

    // Parse sort_by
    const sortByParam = searchParams.get("sort_by");
    if (sortByParam) {
      urlFilters.sort_by = sortByParam;
    }

    // Parse sort_order
    const sortOrderParam = searchParams.get("sort_order");
    if (sortOrderParam) {
      urlFilters.sort_order = sortOrderParam;
    }

    // Parse search query
    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<ServiceAreaFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams, remoteUtilityId, configLevel]);

  // API call using the existing useConfigMapData hook
  const {
    data,
    isLoading: loading,
    error,
  } = useConfigMapData(
    remoteUtilityId,
    "TERRITORY_JSON",
    configLevel,
    "",
    null,
    true,
    filters,
  );

  const handleApplyFilters = (newFilters: Partial<ServiceAreaFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("show_inactive");
    params.delete("created_by");
    params.delete("parent_name");
    params.delete("type_filter");
    params.delete("sort_by");
    params.delete("sort_order");
    params.delete("page");

    // Apply new filters
    if (newFilters.show_inactive !== undefined) {
      params.set("show_inactive", newFilters.show_inactive.toString());
    }

    if (newFilters.created_by) {
      params.set("created_by", newFilters.created_by);
    }

    if (newFilters.parent_name) {
      params.set("parent_name", newFilters.parent_name);
    }

    if (newFilters.type_filter) {
      params.set("type_filter", newFilters.type_filter);
    }

    if (newFilters.sort_by) {
      params.set("sort_by", newFilters.sort_by);
    }

    if (newFilters.sort_order) {
      params.set("sort_order", newFilters.sort_order);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    params.set("limit", itemsPerPage.toString());
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("show_inactive");
    params.delete("created_by");
    params.delete("parent_name");
    params.delete("type_filter");
    params.delete("sort_by");
    params.delete("sort_order");
    params.delete("page");
    params.delete("query");

    params.set("page", "1");
    params.set("limit", itemsPerPage.toString());

    setSearchParams(params);
  };

  // Handle status toggle - for now just console log
  const patchConfigMapMutation = usePatchConfigMap();
  const handleStatusToggle = async (row: any) => {
    try {
      const payload = {
        mapType: "TERRITORY_JSON",
        code: row.code, // required to identify the entity
        remote_utility_id: remoteUtilityId, // fallback if needed
        is_active: !row.isActive, // toggle current status - fixed to use is_active from ServiceAreaItem
      };
      console.log("Toggling status with payload:", payload);
      await patchConfigMapMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Failed to toggle status:", error);
    }
  };

  // Handle view entity
  const handleViewEntity = (entity: ServiceAreaDataItem) => {
    console.log("entity", entity);
    const isUnit = configLevel === "unit" || filters["tab"] === "unit";
    const basePath = isUnit
      ? `/utility/utility-setup/service-area/unit/view`
      : `/utility/utility-setup/service-area/view`;
    const targetCode = encodeURIComponent(
      entity.code || entity.configurationCode || "UNI-UNIT-1",
    );

    navigate(`${basePath}/${targetCode}?tab=${filters["tab"] || configLevel}`, {
      state: { data: entity },
    });
  };

  const handleEdit = (row: ServiceAreaDataItem) => {
    console.log("debug Editing entity:", row);
    setEntityToEdit(row);
    setIsEditModalOpen(true);
  };

  // Handle modal close
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEntityToEdit(null);
  };

  // Handle modal submit (optional - for refresh or callback)
  const handleEditSubmit = (updatedData: any) => {
    console.log("Entity updated:", updatedData);
    // You can add refetch logic here or call onEditEntity
    if (onEditEntity) {
      onEditEntity(updatedData);
    }
  };

  // Helper function to get table columns based on entity type and config level
  const getTableColumns = (): TableColumn<ServiceAreaDataItem>[] => {
    // Base columns that appear in all tables
    const baseColumns: TableColumn<ServiceAreaDataItem>[] = [
      {
        key: "name",
        header: `${entityType} Name`,
        width: "w-64",
        type: "custom",
        render: (_: any, row: ServiceAreaDataItem) => (
          <div className="flex items-center gap-2">
            <div>
              <div className="text-xs font-medium text-gray-900">
                {row.name}
              </div>
              <div className="text-xs text-gray-500">
                Code: {row.configurationCode || "NA"}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "isActive", // Fixed to match API field name
        header: "Status",
        width: "w-32",
        type: "custom",
        render: (value: boolean, row: any) => (
          <div className="flex items-center gap-3">
            <Badge
              variant={value ? "default" : "secondary"}
              className={
                value
                  ? "bg-green-100 text-green-800 border-green-200"
                  : "bg-gray-100 text-gray-800 border-gray-200"
              }
            >
              {value ? "Active" : "Inactive"}
            </Badge>
          </div>
        ),
      },
      {
        key: "createdBy", // Fixed to match API field name
        header: "Created On",
        width: "w-32",
        type: "custom",
        render: (value: string, row) => (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-gray-900">{row.createdDate}</span>
            <span className="text-xs text-gray-700">By: {row.createdBy}</span>
          </div>
        ),
      },
    ];
    switch (entityType?.toLowerCase?.()) {
      case "country":
        baseColumns.push({
          key: "parentName",
          header: "Parent Name",
          width: "w-48",
          type: "custom",
          render: (_: any, row: ServiceAreaDataItem) => (
            <span className="text-xs text-gray-900">
              {row?.hierarchyNames?.region}
            </span>
          ),
        });
        break;

      case "state":
        baseColumns.push({
          key: "parentName",
          header: "Parent Name",
          width: "w-48",
          type: "custom",
          render: (_: any, row: ServiceAreaDataItem) => (
            <span className="text-xs text-gray-900">
              {row?.hierarchyNames?.country}
            </span>
          ),
        });
        break;
      case "city/county":
        baseColumns.push({
          key: "parentName",
          header: "Parent Name",
          width: "w-48",
          type: "custom",
          render: (_: any, row: ServiceAreaDataItem) => (
            <span className="text-xs text-gray-900">
              {row?.hierarchyNames?.state}
            </span>
          ),
        });
        break;
      case "zone":
        baseColumns.push({
          key: "parentName",
          header: "Parent Name",
          width: "w-48",
          type: "custom",
          render: (_: any, row: ServiceAreaDataItem) => (
            <span className="text-xs text-gray-900">
              {row?.hierarchyNames?.county}
            </span>
          ),
        });
        break;
      case "division":
        baseColumns.push({
          key: "parentName",
          header: "Parent Name",
          width: "w-48",
          type: "custom",
          render: (_: any, row: ServiceAreaDataItem) => (
            <span className="text-xs text-gray-900">
              {row?.hierarchyNames?.zone}
            </span>
          ),
        });
        break;
      case "areas":
        baseColumns.push({
          key: "parentName",
          header: "Parent Name",
          width: "w-48",
          type: "custom",
          render: (_: any, row: ServiceAreaDataItem) => (
            <span className="text-xs text-gray-900">
              {row?.hierarchyNames?.division}
            </span>
          ),
        });
        break;
      case "sub-areas":
        baseColumns.push({
          key: "parentName",
          header: "Parent Name",
          width: "w-48",
          type: "custom",
          render: (_: any, row: ServiceAreaDataItem) => (
            <span className="text-xs text-gray-900">
              {row?.hierarchyNames?.area}
            </span>
          ),
        });
        break;
      case "premises":
        baseColumns.push({
          key: "parentName",
          header: "Parent Name",
          width: "w-48",
          type: "custom",
          render: (_: any, row: ServiceAreaDataItem) => (
            <span className="text-xs text-gray-900">
              {row?.hierarchyNames?.subArea}
            </span>
          ),
        });
        break;
      case "unit":
        baseColumns.push({
          key: "parentName",
          header: "Premise",
          width: "w-48",
          type: "custom",
          render: (_: any, row: ServiceAreaDataItem) => (
            <span className="text-xs text-gray-900">
              {row?.hierarchyNames?.premise || (row as any)?.premise || "-"}
            </span>
          ),
        });
        break;
      default:
        break;
    }

    // Config-specific columns
    const configSpecificColumns: TableColumn<ServiceAreaDataItem>[] = [];

    // Add different columns based on config level
    switch (configLevel) {
      case "zone":
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
      case "division":
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
      case "area":
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
      case "sub_area":
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
      case "premise":
        configSpecificColumns.push(
          {
            key: "totalUnits",
            header: "Units",
            width: "w-24",
            type: "custom",
            render: (value: string) => (
              <span className="text-xs text-gray-600">{value || "NA"}</span>
            ),
          },
          {
            key: "installedMeterCount",
            header: "Meters",
            width: "w-32",
            type: "custom",
            render: (value: number) => (
              <span className="text-xs text-gray-600">{value || "NA"}</span>
            ),
          },
          {
            key: "totalConsumerCount",
            header: "Consumers",
            width: "w-32",
            type: "custom",
            render: (value: number) => (
              <span className="text-xs text-gray-600">{value || "NA"}</span>
            ),
          },
        );
        break;
      case "unit":
        configSpecificColumns.push(
          {
            key: "parcelId",
            header: "Parcel ID",
            width: "w-36",
            type: "custom",
            render: (_: any, row: any) => (
              <span className="text-xs font-mono bg-gray-100 border border-gray-200 px-2 py-0.5 rounded text-gray-800 font-medium">
                {row.parcelId || "NA"}
              </span>
            ),
          },
          {
            key: "unitType",
            header: "Type",
            width: "w-32",
            type: "custom",
            render: (_: any, row: any) => {
              const uType = row.unitType || row.type || "NA";
              return (
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-700 border-blue-200 text-xs rounded-full font-medium"
                >
                  {uType}
                </Badge>
              );
            },
          },
        );
        break;
      default:
        // For region, country, state, county - show child count generically
        configSpecificColumns.push({
          key: "childCount",
          header: "Child Count",
          width: "w-24",
          type: "custom",
          render: (value: number) => (
            <span className="text-xs text-gray-600">{value}</span>
          ),
        });
        break;
    }

    // Common columns that appear at the end
    const endColumns: TableColumn<ServiceAreaDataItem>[] = [
      {
        key: "tags", // Fixed to match API field name
        header: "Tags",
        width: "w-32",
        type: "custom",
        render: (value: any) => (
          <div className="flex items-center gap-1">
            {value?.length ? value?.join(", ") : "-"}
          </div>
        ),
      },
      {
        key: "actions",
        header: "Actions",
        sortable: false,
        width: "w-32",
        type: "custom",
        render: (_: any, row: ServiceAreaDataItem) => (
          <div className="flex gap-2 items-center">
            <button
              onClick={() => handleViewEntity(row)}
              className="text-blue-600 hover:text-blue-900 transition-colors"
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </button>
            <Switch
              checked={row.isActive}
              onCheckedChange={() => handleStatusToggle(row)}
            />
            <Edit className="h-4 w-4" onClick={() => handleEdit(row)} />
          </div>
        ),
      },
    ];

    return [...baseColumns, ...configSpecificColumns, ...endColumns];
  };

  const columns = getTableColumns();

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  // Handle search
  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);
    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }
    // Reset to page 1 when searching
    params.set("page", "1");
    setSearchParams(params);
  };

  // Since we're using server-side filtering now, we don't need client-side filtering
  // The API should handle search_data and other filters
  const currentPage = parseInt(searchParams.get("page") || "1");

  const displayData = data?.result || [];
  const totalCount = data?.count || 0;

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          {entityType} Management
        </h2>
        <p className="text-xs text-gray-600">
          Manage and view all {entityType} entities
        </p>
      </div>

      {/* Generic Table */}
      <GenericTable
        data={displayData}
        columns={columns}
        searchPlaceholder={
          entityType?.toLowerCase() === "unit"
            ? "Search unit..."
            : `Search ${entityType}...`
        }
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        rowKey="code"
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil((totalCount || 0) / itemsPerPage),
          totalItems: totalCount || 0,
          itemsPerPage,
          startItem: (currentPage - 1) * itemsPerPage + 1,
          endItem: Math.min(currentPage * itemsPerPage, totalCount || 0),
        }}
        onPageChange={handlePageChange}
        filterPanel={
          <ServiceAreaFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            entityType={entityType}
            configLevel={configLevel}
          />
        }
        emptyMessage={`No ${entityType} found`}
      />

      {isEditModalOpen && entityToEdit && (
        <AddEntityModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          entityType={entityType}
          onSubmit={handleEditSubmit}
          editMode={true}
          editData={entityToEdit}
        />
      )}
    </div>
  );
};

export default ServiceAreaTable;
